import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { authService, AuthUser } from './components/services/auth.service.js';
import { signInWithCustomToken } from 'firebase/auth';
import { firebaseService } from './components/services/firebase.service.js';
import { authSessionService } from './components/services/session.service.js';
import { FUNCTION_ENDPOINTS } from './config/firebase-functions.config.js';

// Import components
import './components/index.js';

// Explicitly import safety components to ensure they're registered
import './components/safety/safety-report-modal/safety-report-modal.js';
import './components/safety/safety-report-viewer/safety-report-viewer.js';
import './components/safety/safety-action-modal/safety-action-modal.js';
import './components/safety/safety-action-viewer/safety-action-viewer.js';

// Import data-table component to ensure it's registered
import './components/common/data-table/data-table.js';

// Import user management components
import './components/users/users-page/users-page.js';

// Import profile page component
import './components/profile/profile-page.js';
import './components/users/user-modal/user-modal.js';

// Import asset detail page
import './components/assets/asset-detail-page/asset-detail-page.js';

// Import role-based dashboards
import './components/dashboard/role-based/shop-floor-dashboard.js';
import './components/dashboard/role-based/engineer-dashboard.js';

import { getUserByUid, getCompanyById } from '@cmms/api';
import { SubscriptionPlanCat } from '@cmms/models';

// HMR setup with custom element handling
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    // Force full page reload on changes to avoid custom element re-registration errors
    window.location.reload();
  });
}

// Use the exported instance directly (it's a singleton)
const authServiceInstance = authService;

@customElement('cmms-app')
export class CMMSApp extends LitElement {
  @state()
  private isLoggedIn = false;

  @state()
  private user: AuthUser | null = null;

  @state()
  private isLoading = true;
  
  @state()
  private sidebarCollapsed = true;
  
  @state()
  private currentPageTitle = this.getStoredPageTitle();
  
  @state()
  private isUsingEmulator = false;
  
  @state()
  private showSettings = false;

  @state()
  private showSignup = false;

  @state()
  private showAssetDetail = false;

  @state()
  private currentAssetId = '';
  
  @state()
  private companySubscriptionPlan: SubscriptionPlanCat = SubscriptionPlanCat.STARTER;
  
  @property({ type: String }) companyId: string = '';

  private unsubscribeAuth: (() => void) | null = null;

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      font-family: var(--sl-font-sans);
      background: var(--sl-color-neutral-50);
    }
    
    .app-layout {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    }
    
    
    
    .loading {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
    }
    
    .emulator-indicator {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, #ff4444, #cc0000);
      color: white;
      padding: 8px 16px;
      text-align: center;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.5px;
      box-shadow: 0 -2px 10px rgba(255, 68, 68, 0.3);
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    
    .emulator-indicator::before {
      content: '🔥';
      font-size: 16px;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.setupAuthListener();
    this.setupSessionManagement();
    this.isUsingEmulator = firebaseService.isUsingEmulator();

    // Check current URL and set initial state
    this.checkCurrentRoute();

    // Listen for browser back/forward navigation
    window.addEventListener('popstate', (e) => {
      this.checkCurrentRoute();
    });

    // Listen for navigation events from profile page
    window.addEventListener('navigate', (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail.page) {
        this.handlePageNavigate(customEvent);
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  }

  private setupAuthListener() {
    // Listen for authentication state changes
    this.unsubscribeAuth = authServiceInstance.onAuthStateChanged((user) => {
      this.user = user;
      this.isLoggedIn = user !== null;
      this.isLoading = false;
      
      // Only call getUser() when we have an authenticated user
      if (user && user.uid) {
        this.getUser();
      }
    });
  }

  private handleLoginSuccess(e: CustomEvent) {
    // This will be handled by the auth state listener
  }

  private setupSessionManagement() {
    // Listen for session expiration
    authSessionService.onLogout(() => {
      this.handleSessionExpired();
    });
  }

  private handleSessionExpired() {
    
    this.handleLogout();
  }

  private handleLogout() {
    // Reset company ID when user logs out
    this.companyId = '';
    // The auth state listener will handle showing the login screen
  }

  private handleSidebarToggle() {
    
    this.sidebarCollapsed = !this.sidebarCollapsed;
    
  }

  private checkCurrentRoute() {
    const path = window.location.pathname;
    if (path === '/settings') {
      this.showSettings = true;
      this.showAssetDetail = false;
      this.currentPageTitle = 'Settings';
    } else if (path === '/signup') {
      this.showSignup = true;
      this.showAssetDetail = false;
      this.currentPageTitle = 'Sign Up';
    } else if (path.startsWith('/assets/')) {
      // Extract asset ID from path
      const assetId = path.split('/assets/')[1];
      if (assetId) {
        this.showAssetDetail = true;
        this.currentAssetId = assetId;
        this.showSettings = false;
        this.currentPageTitle = 'Asset Details';
      }
    } else {
      this.showSettings = false;
      this.showSignup = false;
      this.showAssetDetail = false;
      this.currentPageTitle = 'Dashboard';
    }
  }

  private handleSettingsRequested() {
    this.showSettings = true;
    this.currentPageTitle = 'Settings';
    // Update URL to /settings
    window.history.pushState({ page: 'settings' }, '', '/settings');
  }

  private async handleSignupRequest(e: CustomEvent) {
    console.log('Signup request received:', e.detail);
    
    try {
      const { company, user } = e.detail;
      
      // Call the backend signup function
      const response = await fetch(FUNCTION_ENDPOINTS.SIGNUP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company: e.detail.company,
          user: e.detail.user
        })
      });

      const result = await response.json();

      if (result.success) {
        // Signup successful - login with custom token
        console.log('Signup successful, logging in...');
        
        // Get auth instance
        const auth = firebaseService.getAuth();
        
        // Sign in with custom token
        const userCredential = await signInWithCustomToken(auth, result.authData.customToken);
        
        // Store user data
        this.user = {
          uid: userCredential.user.uid,
          email: userCredential.user.email || '',
          displayName: userCredential.user.displayName || '',
          companyId: result.authData.user.company_id
        };
        
        this.isLoggedIn = true;
        this.showSignup = false;
        
        // Store in localStorage
        localStorage.setItem('cmms_user', JSON.stringify(this.user));
        localStorage.setItem('cmms_logged_in', 'true');
        
        console.log('Login successful after signup');
        
        // Redirect to dashboard
        window.location.href = '/';
        
      } else {
        throw new Error(result.error || 'Signup failed');
      }
      
    } catch (error: any) {
      console.error('Signup error:', error);
      // Show error in the signup component instead of alert
      const signupComponent = document.querySelector('cmms-signup');
      if (signupComponent) {
        (signupComponent as any).errorMessage = `Signup failed: ${error.message}`;
        (signupComponent as any).requestUpdate();
      }
    }
  }

  private getStoredPageTitle(): string {
    try {
      const stored = localStorage.getItem('cmms_current_page');
      const pageTitles: {[key: string]: string} = {
        'dashboard': 'Dashboard',
        'work-orders': 'Work Orders',
        'assets': 'Assets',
        'locations': 'Locations',
        'inventory': 'Inventory',
        'preventive-maintenance': 'Preventive Maintenance',
        'incidents': 'H&S (Incidents)',
        'technical': 'Technical',
        'gmp-audit': 'Technical',
        'gmp': 'Technical',
        'reports': 'Reports',
        'admin': 'Admin / Settings'
      };
      return pageTitles[stored || 'dashboard'] || 'Dashboard';
    } catch (error) {
      console.warn('Failed to get stored page title:', error);
      return 'Dashboard';
    }
  }

  private handlePageNavigate(e: CustomEvent) {
    const page = e.detail.page;
    
    // If navigating to any page other than settings, hide settings
    if (page !== 'settings') {
      this.showSettings = false;
      this.showAssetDetail = false;
      // Update URL to dashboard
      window.history.pushState({ page: page }, '', '/');
    }
    
    const pageTitles: {[key: string]: string} = {
      'dashboard': 'Dashboard',
      'work-orders': 'Work Orders',
      'assets': 'Assets',
      'locations': 'Locations',
      'inventory': 'Inventory',
      'preventive-maintenance': 'Preventive Maintenance',
      'incidents': 'H&S (Incidents)',
      'technical': 'Technical',
      'gmp-audit': 'Technical',
      'users': 'User Management',
      'settings': 'Settings',
      'gmp': 'Technical',
      'reports': 'Reports',
      'admin': 'Admin / Settings'
    };
    this.currentPageTitle = pageTitles[page] || 'Dashboard';
  }

  private handleAssetNavigateBack() {
    this.showAssetDetail = false;
    this.currentAssetId = '';
    this.currentPageTitle = 'Dashboard';
    window.history.pushState({ page: 'dashboard' }, '', '/');
  }

  private async getUser() {
    if (!this.user?.uid) {
      console.warn('Cannot get user: no user ID available');
      return null;
    }
    
    const user = await getUserByUid(firebaseService.getFirestore(), this.user.uid);
    
    
    
    this.companyId = (user.data as any)?.model?.company_id;
    
    // Fetch company subscription plan if we have a company ID
    if (this.companyId) {
      await this.fetchCompanySubscriptionPlan();
    }
    
    return user;
  }

  private async fetchCompanySubscriptionPlan() {
    if (!this.companyId) return;
    
    try {
      const result = await getCompanyById(firebaseService.getFirestore(), this.companyId);
      
      if (result.data && !result.error && (result.data as any).model) {
        this.companySubscriptionPlan = (result.data as any).model.subscription_plan_cat || SubscriptionPlanCat.STARTER;
      } else {
        this.companySubscriptionPlan = SubscriptionPlanCat.STARTER;
      }
    } catch (error) {
      console.error('Error fetching company subscription plan:', error);
      this.companySubscriptionPlan = SubscriptionPlanCat.STARTER;
    }
  }

  private shouldRenderShopFloorDashboard(): boolean {
    // Check if user has shop floor role
    const userRole = this.user?.role?.toLowerCase();
    return userRole === 'shop floor' || userRole === 'shopfloor';
  }

  private shouldRenderEngineerDashboard(): boolean {
    // Check if user has engineer role
    const userRole = this.user?.role?.toLowerCase();
    return userRole === 'engineer';
  }

  render() {
    if (this.isLoading) {
      return html`
        <div class="loading">
          <sl-spinner size="large"></sl-spinner>
        </div>
        ${this.isUsingEmulator ? html`
          <div class="emulator-indicator">
            Firebase Emulator Mode
          </div>
        ` : ''}
      `;
    }

    if (!this.isLoggedIn) {
      if (this.showSignup) {
        return html`
          <cmms-signup @signup-request=${this.handleSignupRequest}></cmms-signup>
          ${this.isUsingEmulator ? html`
            <div class="emulator-indicator">
              Firebase Emulator Mode
            </div>
          ` : ''}
        `;
      }
      
      return html`
        <cmms-login @login-success=${this.handleLoginSuccess}></cmms-login>
        ${this.isUsingEmulator ? html`
          <div class="emulator-indicator">
            Firebase Emulator Mode
          </div>
        ` : ''}
      `;
    }

    return html`
      <div class="app-layout">
        <cmms-header 
          pageTitle=${this.currentPageTitle}
          companyId=${this.companyId}
          subscriptionPlan=${this.companySubscriptionPlan}
          ?showSidebarToggle=${!this.showAssetDetail && !this.shouldRenderShopFloorDashboard() && !this.shouldRenderEngineerDashboard()}
          ?isSettingsPage=${this.showSettings}
          @sidebar-toggle=${this.handleSidebarToggle}
          @profile-requested=${this.handleSettingsRequested}
          @navigate=${this.handlePageNavigate}
        ></cmms-header>
        
        <div class="app-content">
          ${this.showSettings ? html`
            <profile-page .companyId=${this.companyId}></profile-page>
          ` : this.showAssetDetail ? html`
            <asset-detail-page
              .assetId=${this.currentAssetId}
              .companyId=${this.companyId}
              .firestore=${firebaseService.getFirestore()}
              .userRole=${this.user?.role || ''}
              @navigate-back=${this.handleAssetNavigateBack}
            ></asset-detail-page>
          ` : this.shouldRenderShopFloorDashboard() ? html`
            <shop-floor-dashboard 
              .companyId=${this.companyId}
              .user=${this.user}
              .firestore=${firebaseService.getFirestore()}
            ></shop-floor-dashboard>
          ` : this.shouldRenderEngineerDashboard() ? html`
            <engineer-dashboard 
              .companyId=${this.companyId}
              .user=${this.user}
              .firestore=${firebaseService.getFirestore()}
            ></engineer-dashboard>
          ` : html`
            <cmms-dashboard 
              .companyId=${this.companyId}
              .user=${this.user}
              .sidebarCollapsed=${this.sidebarCollapsed}
              .firestore=${firebaseService.getFirestore()}
              @user-logged-out=${this.handleLogout}
              @navigate=${this.handlePageNavigate}
            ></cmms-dashboard>
          `}
        </div>
      </div>
      
      ${this.isUsingEmulator ? html`
        <div class="emulator-indicator">
          Firebase Emulator Mode
        </div>
      ` : ''}
    `;
  }
}

// Initialize the app
document.addEventListener('DOMContentLoaded', () => {
  // Check if app already exists to prevent duplicates
  if (!document.querySelector('cmms-app')) {
    const app = document.createElement('cmms-app');
    document.getElementById('app')?.appendChild(app);
  }

  // Register Service Worker for PWA
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('[PWA] Service Worker registered successfully:', registration.scope);
        })
        .catch((error) => {
          console.error('[PWA] Service Worker registration failed:', error);
        });
    });
  }
}); 
